'use client';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	type PropsWithChildren,
} from 'react';

export interface CookieConsent {
	necessary: boolean;
	analytics: boolean;
	marketing: boolean;
}

interface CookieConsentContextType {
	consent: CookieConsent | null;
	updateConsent: (newConsent: CookieConsent) => void;
	loading: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(
	undefined,
);

export function CookieConsentProvider({children}: PropsWithChildren) {
	const [consent, setConsent] = useState<CookieConsent | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const storedConsent = localStorage.getItem('cookieConsent');
		if (storedConsent) {
			setConsent(JSON.parse(storedConsent));
		}
		setLoading(false);
	}, []);

	const updateConsent = (newConsent: CookieConsent) => {
		setConsent(newConsent);
		localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
	};

	return (
		<CookieConsentContext.Provider value={{consent, updateConsent, loading}}>
			{children}
		</CookieConsentContext.Provider>
	);
}

export function useCookieConsent() {
	const context = useContext(CookieConsentContext);
	if (context === undefined) {
		throw new Error('useCookieConsent must be used within a CookieConsentProvider');
	}
	return context;
}
